import React, { useState, useEffect, useCallback } from 'react';
import { getPendingReviews } from '../services/reviewServices';
import { useAuth } from '../../USER/Auth/AuthContext';
import ReviewModal from './ReviewModal';
import { QRCodeSVG } from 'qrcode.react';
import { SERVICE_TYPES, ROLES } from '../../../constants';
import { BryCard, BryButton } from '../../UI/BryUI';
import { Star, Bike, Package, AlertTriangle, MapPin } from 'lucide-react';

const PendingReviews = () => {
  const { currentUser, isDriver } = useAuth();
  const [pendingServices, setPendingServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  const fetchPendingReviews = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const userType = isDriver ? ROLES.DRIVER : 'customer';
      const data = await getPendingReviews(currentUser.id, userType);
      setPendingServices(data || []);
    } catch (error) {
      console.error('Error fetching pending reviews:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser, isDriver]);

  useEffect(() => {
    fetchPendingReviews();
  }, [fetchPendingReviews]);

  const handleReviewClick = (service) => {
    setSelectedService(service);
    setShowReviewModal(true);
  };

  const getServiceIcon = (type) => {
    if (type === SERVICE_TYPES.DELIVERY) {
      return <Package size={14} className="mr-1.5" />;
    }
    return <Bike size={14} className="mr-1.5" />;
  };

  if (loading || pendingServices.length === 0) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-gray-950 z-[99999] overflow-y-auto pb-20 animate-in fade-in duration-300">
      {isDriver ? (
        <div className="bg-[var(--bry-accent)]/20 text-[var(--bry-accent)] p-3 text-center font-bold sticky top-0 shadow-sm border-b border-[var(--bry-accent)]/30 flex items-center justify-center gap-2 text-sm z-10 backdrop-blur-md">
          <AlertTriangle size={18} />
          ¡Evaluaciones Pendientes!
        </div>
      ) : (
        <div className="bg-yellow-500/20 text-yellow-400 p-3 text-center font-bold sticky top-0 shadow-sm border-b border-yellow-500/30 flex items-center justify-center gap-2 text-sm z-10 backdrop-blur-md">
          <AlertTriangle size={18} />
          ¡Tu opinión importa! Evalúa tus servicios recientes.
        </div>
      )}
      
      <div className="px-4 mt-6">
        <h5 className="font-bold mb-5 flex items-center gap-2 text-white text-lg">
          <Star size={20} className={isDriver ? "text-[var(--bry-accent)] fill-[var(--bry-accent)]" : "text-yellow-400 fill-yellow-400"} />
          Evaluaciones ({pendingServices.length})
        </h5>
        
        <div className="space-y-6">
          {pendingServices.map(service => {
            if (isDriver) {
              return (
                <BryCard key={service.service_id} className="border-t-4 border-t-[var(--bry-accent)] border-gray-800 bg-gray-950 shadow-2xl overflow-hidden relative">
                  <div className="absolute top-0 left-0 w-32 h-32 bg-[var(--bry-accent)]/5 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>
                  <div className="p-5 relative z-10">
                    <div className="text-center mb-5">
                      <span className="inline-flex items-center bg-[var(--bry-accent)]/10 text-[var(--bry-accent)] text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider mb-3">
                        {getServiceIcon(service.type)}
                        Servicio Completado
                      </span>
                      <h4 className="font-black text-3xl text-white tracking-tight">${service.price}</h4>
                      <p className="text-gray-500 text-xs mt-1 font-medium">{service.distance} km • {new Date(service.created_at).toLocaleDateString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute:'2-digit' })}</p>
                    </div>
                    
                    <div className="bg-white p-5 rounded-2xl flex flex-col items-center justify-center mb-5 shadow-[0_0_20px_rgba(14,171,176,0.15)] mx-auto max-w-[200px]">
                      <QRCodeSVG 
                        value={`${window.location.origin}/evaluaciones?serviceId=${service.service_id}`} 
                        size={140} 
                        level={"M"}
                        includeMargin={false}
                      />
                      <p className="text-gray-900 font-black text-sm mt-3 text-center uppercase tracking-wider">Escanear</p>
                      <p className="text-gray-500 text-[10px] text-center leading-tight mt-1 font-semibold">Para pasajero</p>
                    </div>

                    <BryButton 
                      variant="outline" 
                      className="w-full border-[var(--bry-accent)]/50 text-[var(--bry-accent)] hover:bg-[var(--bry-accent)]/10 font-bold py-3"
                      onClick={() => handleReviewClick(service)}
                    >
                      <Star size={18} className="mr-2" /> Evaluar Pasajero
                    </BryButton>
                  </div>
                </BryCard>
              );
            }
            
            // Passenger View
            return (
              <BryCard key={service.service_id} className="border-l-4 border-l-yellow-400 border-gray-800 bg-gray-900 shadow-xl overflow-hidden relative">
                <div className="absolute top-0 right-0 w-24 h-24 bg-yellow-400/5 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                
                <div className="p-4 relative z-10">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <span className="inline-flex items-center bg-yellow-500/20 text-yellow-400 text-[10px] font-bold px-2 py-1 rounded-md uppercase tracking-wider mb-2">
                        {getServiceIcon(service.type)}
                        {service.type === SERVICE_TYPES.DELIVERY ? 'Entrega' : 'Viaje'}
                      </span>
                      <h4 className="font-black m-0 text-2xl text-white">${service.price}</h4>
                    </div>
                    <div className="text-right flex flex-col items-end">
                      <span className="text-gray-500 text-[10px] uppercase font-bold tracking-widest mb-1">Distancia</span>
                      <span className="text-lg font-bold text-gray-200">{service.distance} km</span>
                    </div>
                  </div>

                  <div className="space-y-3 mb-5">
                    <div className="flex items-start gap-3 bg-gray-800/60 p-3 rounded-xl border border-gray-700/50">
                      <div className="bg-[var(--bry-accent)]/20 p-2 rounded-full shrink-0 mt-0.5">
                        <MapPin size={14} className="text-[var(--bry-accent)]" />
                      </div>
                      <div>
                        <span className="block text-gray-500 text-[10px] uppercase font-bold tracking-wider mb-0.5">Recogida</span>
                        <span className="block text-sm text-gray-200 font-medium leading-tight line-clamp-1">{service.pickup?.address || 'Ubicación GPS'}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 bg-gray-800/60 p-3 rounded-xl border border-gray-700/50">
                      <div className="bg-gray-700 p-2 rounded-full shrink-0 mt-0.5">
                        <MapPin size={14} className="text-gray-300" />
                      </div>
                      <div>
                        <span className="block text-gray-500 text-[10px] uppercase font-bold tracking-wider mb-0.5">Entrega</span>
                        <span className="block text-sm text-gray-200 font-medium leading-tight line-clamp-1">{service.dropoff?.address || 'Destino en Mapa'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center mb-3">
                    <span className="text-xs text-gray-500 font-medium">
                      {new Date(service.created_at).toLocaleDateString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute:'2-digit' })}
                    </span>
                  </div>
                  
                  <BryButton 
                    variant="primary" 
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-black border-0 shadow-lg shadow-yellow-500/20 font-bold py-3"
                    onClick={() => handleReviewClick(service)}
                  >
                    <Star size={18} className="mr-2 fill-black" /> Evaluar Conductor
                  </BryButton>
                </div>
              </BryCard>
            );
          })}
        </div>
      </div>

      {selectedService && (
        <ReviewModal
          show={showReviewModal}
          onHide={() => {
            setShowReviewModal(false);
            setSelectedService(null);
            fetchPendingReviews();
          }}
          service={selectedService}
          reviewType={isDriver ? 'driver_to_customer' : 'customer_to_driver'}
        />
      )}
    </div>
  );
};

export default PendingReviews;
